import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { docsClient, Document } from "@/lib/supabase";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { FileText, Home, Tag, Download, ExternalLink } from "lucide-react";

interface SearchResult extends Document {
  rank?: number;
}

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Register Cmd+K / Ctrl+K keyboard shortcut
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    if (query.length > 2) {
      searchDocuments();
    } else {
      setSearchResults([]);
    }
  }, [query]);

  const searchDocuments = async () => {
    setLoading(true);
    try {
      const { data, error } = await (
        docsClient.rpc as (
          name: string,
          params: Record<string, unknown>
        ) => Promise<{ data: SearchResult[] | null; error: unknown | null }>
      )("search_documents", {
        p_query: query,
        p_limit: 10,
      });

      if (error) throw error;

      setSearchResults(data || []);
    } catch (error) {
      console.error("Error searching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (path: string) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  const handleOpenDocument = (url: string) => {
    window.open(url, "_blank");
    setOpen(false);
    setQuery("");
  };

  const handleViewDocument = (id: string) => {
    setOpen(false);
    setQuery("");
    navigate(`/documents?id=${id}`);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Type to search documents or navigate..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>{loading ? "Searching..." : "No results found."}</CommandEmpty>

        {/* Navigation */}
        {!query && (
          <>
            <CommandGroup heading="Navigation">
              <CommandItem onSelect={() => handleNavigate("/dashboard")}>
                <Home className="mr-2 h-4 w-4" />
                <span>Dashboard</span>
              </CommandItem>
              <CommandItem onSelect={() => handleNavigate("/documents")}>
                <FileText className="mr-2 h-4 w-4" />
                <span>Documents</span>
              </CommandItem>
              <CommandItem onSelect={() => handleNavigate("/tags")}>
                <Tag className="mr-2 h-4 w-4" />
                <span>Tags</span>
              </CommandItem>
              <CommandItem onSelect={() => handleNavigate("/exports")}>
                <Download className="mr-2 h-4 w-4" />
                <span>Exports</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <CommandGroup heading="Documents">
            {searchResults.map((doc) => (
              <CommandItem
                key={doc.id}
                onSelect={() => handleViewDocument(doc.id)}
                className="flex items-start gap-2"
              >
                <FileText className="mt-1 h-4 w-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{doc.title || "Untitled"}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDocument(doc.url);
                      }}
                      className="flex-shrink-0 text-primary hover:text-primary/80"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                  {doc.description && (
                    <p className="text-xs text-muted-foreground truncate">{doc.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {doc.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Hints */}
        {!query && (
          <CommandGroup heading="Tips">
            <CommandItem disabled>
              <span className="text-xs text-muted-foreground">
                Type to search documents by title, description, or content
              </span>
            </CommandItem>
            <CommandItem disabled>
              <span className="text-xs text-muted-foreground">
                Use ↑↓ to navigate, Enter to select, Esc to close
              </span>
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
